import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useCogsSettings, useCogsSettingsAuditLogs, useUpdateCogsSettings } from '@/api/cogs-settings'
import type { CogsSettings, CogsSettingsBody } from '@/api/cogs-settings'
import { useWorkspace } from '@/api/workspace'
import { bpsToPercentInput, isPercent, percentToBps } from '@/components/cogs/percent'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { RecordHistory } from '@/components/record-history/record-history'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// COGS settings (UI-SCREENS C-03, 08a #202): the five Workspace COGS inputs, Owner only, with the
// History of their changes. Percentages are typed as percent and sent as bps, money as whole Rp;
// a blank field stays null (#202 Q3).
export const Route = createFileRoute('/w/$slug/settings/cogs')({
  component: CogsSettingsScreen,
  staticData: { breadcrumb: 'cogs.settings.title' },
})

function CogsSettingsScreen() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const workspace = useWorkspace(slug)
  const settings = useCogsSettings(slug)
  const history = useCogsSettingsAuditLogs(slug)
  if (workspace.data?.role !== 'owner') return <Navigate to="/w/$slug" params={{ slug }} replace />

  return (
    <section className="flex max-w-xl flex-col gap-4">
      <h1 className="font-heading text-xl font-semibold">{t('cogs.settings.title')}</h1>
      <Card>
        <CardHeader>
          <CardDescription>{t('cogs.settings.explanation')}</CardDescription>
        </CardHeader>
        <CardContent>
          {settings.isPending ? (
            <LoadingSkeleton variant="form" rows={4} />
          ) : settings.isError || !settings.data ? (
            <p role="alert" className="text-sm text-destructive">
              {t('errors.generic')}
            </p>
          ) : (
            <SettingsForm slug={slug} settings={settings.data} />
          )}
        </CardContent>
      </Card>
      <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} />
    </section>
  )
}

type Values = {
  laborRate: string
  electricityPrice: string
  maintenance: string
  targetMargin: string
  defaultYield: string
}

const WHOLE = /^\d+$/
const INTEGER_MAX = 2_147_483_647

function schemaFor(t: (key: string) => string) {
  const whole = z
    .string()
    .trim()
    .refine((value) => value === '' || (WHOLE.test(value) && Number(value) <= INTEGER_MAX), {
      message: t('cogs.settings.wholeInvalid'),
    })
  const percent = (ok: (bps: number) => boolean, message: string) =>
    z
      .string()
      .trim()
      .refine((value) => value === '' || (isPercent(value) && ok(percentToBps(value))), { message: t(message) })

  return z.object({
    laborRate: whole,
    electricityPrice: whole,
    maintenance: percent((bps) => bps <= INTEGER_MAX, 'cogs.settings.percentInvalid'),
    targetMargin: percent((bps) => bps < 10_000, 'cogs.settings.marginInvalid'),
    defaultYield: percent((bps) => bps > 0 && bps <= 10_000, 'cogs.settings.yieldInvalid'),
  })
}

function defaults(settings: CogsSettings): Values {
  const money = (value: number | null) => (value === null ? '' : String(value))
  return {
    laborRate: money(settings.labor_rate_per_hour),
    electricityPrice: money(settings.electricity_price_per_kwh),
    maintenance: bpsToPercentInput(settings.maintenance_bps),
    targetMargin: bpsToPercentInput(settings.target_margin_bps),
    defaultYield: bpsToPercentInput(settings.default_yield_bps),
  }
}

const toWhole = (value: string) => (value.trim() === '' ? null : Number(value.trim()))
const toBps = (value: string) => (value.trim() === '' ? null : percentToBps(value))

function SettingsForm({ slug, settings }: { slug: string; settings: CogsSettings }) {
  const { t } = useTranslation()
  const update = useUpdateCogsSettings()
  const form = useForm<Values>({ resolver: zodResolver(schemaFor(t)), defaultValues: defaults(settings) })
  const errors = form.formState.errors

  const onSubmit = form.handleSubmit((values) => {
    const body: CogsSettingsBody = {
      cogs_settings: {
        labor_rate_per_hour: toWhole(values.laborRate),
        electricity_price_per_kwh: toWhole(values.electricityPrice),
        maintenance_bps: toBps(values.maintenance),
        target_margin_bps: toBps(values.targetMargin),
        default_yield_bps: toBps(values.defaultYield),
      },
    }
    update.mutate({ params: { path: { slug } }, body }, { onSuccess: (data) => form.reset(defaults(data)) })
  })

  const fields: { name: keyof Values; label: string; hint: string; decimal: boolean }[] = [
    { name: 'laborRate', label: 'cogs.settings.laborRate', hint: 'cogs.settings.laborRateHint', decimal: false },
    {
      name: 'electricityPrice',
      label: 'cogs.settings.electricityPrice',
      hint: 'cogs.settings.electricityPriceHint',
      decimal: false,
    },
    { name: 'maintenance', label: 'cogs.settings.maintenance', hint: 'cogs.settings.maintenanceHint', decimal: true },
    { name: 'targetMargin', label: 'cogs.settings.targetMargin', hint: 'cogs.settings.targetMarginHint', decimal: true },
    { name: 'defaultYield', label: 'cogs.settings.defaultYield', hint: 'cogs.settings.defaultYieldHint', decimal: true },
  ]

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {update.isSuccess && !form.formState.isDirty ? t('cogs.settings.saved') : ''}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {update.isError ? t('cogs.settings.invalid') : ''}
      </p>

      {fields.map((field) => {
        const id = `cogs-${field.name}`
        const error = errors[field.name]?.message ?? ''
        return (
          <div key={field.name} className="flex flex-col gap-2">
            <Label htmlFor={id}>{t(field.label)}</Label>
            <Input
              id={id}
              className="h-11"
              inputMode={field.decimal ? 'decimal' : 'numeric'}
              autoComplete="off"
              aria-invalid={Boolean(error)}
              aria-describedby={`${id}-hint ${id}-error`}
              {...form.register(field.name)}
            />
            <p id={`${id}-hint`} className="text-sm text-muted-foreground">
              {t(field.hint)}
            </p>
            <p id={`${id}-error`} role="alert" className="text-sm text-destructive empty:hidden">
              {error}
            </p>
          </div>
        )
      })}

      <Button type="submit" className="h-11 self-start" disabled={update.isPending}>
        {t(update.isPending ? 'cogs.settings.saving' : 'cogs.settings.save')}
      </Button>
    </form>
  )
}

